<template>
  <ToolTip :has-content="!!info">
    <template #trigger>
      <div
        v-bind="$attrs"
        data-el="form-badge"
        style="line-height: 1rem"
        :class="{
          [classes]: true,
          'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xxs font-medium ring-1 ring-inset': true,
        }"
        :title="title"
      >
        <Icon
          class="text-xxs"
          v-if="icon && iconPosition === 'left'"
          :icon="icon"
        />
        <slot>
          <span>
            {{ title }}
          </span>
        </slot>
        <Icon
          class="text-xxs"
          v-if="icon && iconPosition === 'right'"
          :icon="icon"
        />
      </div>
    </template>
    <template #content>
      {{ info }}
    </template>
  </ToolTip>
</template>
<script lang="ts" setup>
import { computed } from 'vue';

import { Variant } from '~/store/_types';
import type { FormSize, MsIcon } from '~/lib/services/types';
import { assertUnreachable } from '~/lib/services/assertUnreachable';
import ToolTip from './ToolTip.vue';

const props = withDefaults(
  defineProps<{
    variant?: Variant;
    title?: string;
    size?: FormSize;
    info?: string;
    icon?: MsIcon;
    self?: 'start' | 'center';
    iconPosition?: 'left' | 'right';
  }>(),
  {
    iconPosition: 'right',
    icon: undefined,
    variant: Variant.light,
    title: undefined,
    info: '',
    size: 'sm',
    self: 'center',
  },
);
const classes = computed(() => {
  switch (props.variant) {
    case Variant.white:
      return 'bg-white text-gray-900 ring-gray-300';
    case Variant.light:
    case Variant.secondary:
      return 'bg-gray-50 text-gray-600 ring-gray-500/10';
    case Variant.danger:
      return 'bg-red-50 text-red-700 ring-red-600/10';
    case Variant.warning:
      return 'bg-yellow-50 text-yellow-800 ring-yellow-600/20';
    case Variant.success:
      return 'bg-green-50 text-green-700 ring-green-600/20';
    case Variant.primary:
      return 'bg-blue-50 text-blue-700 ring-blue-700/10';
    case Variant.info:
      return 'bg-indigo-50 text-indigo-700 ring-indigo-700/10';
    case Variant.upgrade:
      return 'bg-purple-50 text-purple-700 ring-purple-700/10';
    default:
      return assertUnreachable(props.variant);
  }
});
defineOptions({
  inheritAttrs: false,
});
</script>
